import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import "./css/main.login.css";
import "./css/login-page.css";
import { getAssetUrl } from "../../utils/assetUrl";

const registerSchema = yup.object().shape({
  username: yup.string().required("Tên tài khoản bắt buộc"),
  phone_number: yup
    .string()
    .matches(/^\d{10}$/, "Số điện thoại phải có 10 chữ số")
    .required("Số điện thoại bắt buộc"),
  password: yup.string().min(6, "Mật khẩu ít nhất 6 ký tự").required(),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], "Mật khẩu không khớp")
    .required("Vui lòng nhập lại mật khẩu"),
});

type RegisterFormValues = yup.InferType<typeof registerSchema>;

const Login: React.FC = () => {
  const Cookies = require("js-cookie");

  const [authView, setAuthView] = useState<"login" | "register">("login");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegPassword2, setShowRegPassword2] = useState(false);

  const {
    register: registerField,
    handleSubmit: handleRegisterSubmit,
    reset: resetRegisterForm,
    formState: { errors: registerErrors },
  } = useForm<RegisterFormValues>({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      username: "",
      phone_number: "",
      password: "",
      password_confirmation: "",
    },
  });

  useEffect(() => {
    document.documentElement.classList.add("login-scroll-lock");
    document.body.classList.add("login-scroll-lock");
    return () => {
      document.documentElement.classList.remove("login-scroll-lock");
      document.body.classList.remove("login-scroll-lock");
    };
  }, []);

  const handleLogin = async () => {
    if (userName.trim() === "" || password.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Lỗi đăng nhập",
        text: "Vui lòng điền đầy đủ thông tin",
        customClass: {
          popup: "custom-swal",
          title: "custom-title",
          htmlContainer: "custom-text",
        },
      });
      return;
    } else if (userName.trim() != "" || password.trim() != "") {
      try {
        Swal.fire({
          title: "Đang xử lý...",
          html: "<p class='swal-loading-subtext'>Vui lòng chờ trong giây lát</p><div class='swal-loading-dots'><span class='swal-dot swal-dot-cyan'></span><span class='swal-dot swal-dot-orange'></span></div>",
          customClass: {
            popup: "custom-swal swal-loading-modal",
            title: "custom-title",
            htmlContainer: "custom-text",
          },
          allowOutsideClick: false,
          showConfirmButton: false,
        });

        await new Promise((resolve) => setTimeout(resolve, 1000));
        await axios
          .post(`${process.env.REACT_APP_URL_API}/auth/login`, {
            username: userName,
            password,
          })
          .then(async (data) => {
            localStorage.setItem(
              "user_info",
              JSON.stringify({
                userName: data.data.user.username,
                coins: data.data.user.coins,
                role: data.data.user.role,
                id: data.data.user._id,
              })
            );

            Cookies.set("access_token", data.data.access_token, {
              expires: 1 / 24,
              secure: false,
              sameSite: "Lax",
            });

            await new Promise((resolve) => setTimeout(resolve, 100));

            if (data.status === 201 && (data.data.user.role === "SUPERADMIN" || data.data.user.role === "ADMIN")) {
              Swal.fire({
                icon: "success",
                title: "Đăng nhập thành công",
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
              setTimeout(() => {
                window.location.href = "/admin";
              }, 500);
            } else {
              Swal.fire({
                icon: "success",
                title: "Đăng nhập thành công",
                text: "Vui lòng đợi ít phút. ",
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
              setTimeout(() => {
                window.location.href = "/";
              }, 500);
            }
          })
          .catch((err) => {
            if (err.response?.status === 401) {
              Swal.fire({
                icon: "error",
                title: "Lỗi đăng nhập",
                text: "Tài khoản/mật khẩu không chính xác!",
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
            }
          });
      } catch (error) {
        return error;
      }
    }
  };

  const onRegister = async (data: RegisterFormValues) => {
    try {
      await axios
        .post(`${process.env.REACT_APP_URL_API}/auth/register`, {
          username: data.username,
          managedByUsername: "superadmin",
          phone: data.phone_number,
          password: String(data.password),
        })
        .then((res) => {
          if (res.status === 201) {
            Swal.fire({
              icon: "success",
              title: "Đăng ký thành công",
              text: "Vui lòng đợi ít phút. ",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
            resetRegisterForm();
            setAuthView("login");
          }
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Lỗi đăng ký",
            text: `${err.response?.data?.message || "Có lỗi xảy ra, vui lòng thử lại!"}`,
            customClass: {
              popup: "custom-swal",
              title: "custom-title",
              htmlContainer: "custom-text",
            },
          });
        });
    } catch (error) {
      return error;
    }
  };

  const goRegister = () => {
    setAuthView("register");
    resetRegisterForm();
  };

  const goLogin = () => {
    setAuthView("login");
    resetRegisterForm();
  };

  const eyeIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"
        stroke="currentColor"
        strokeWidth="1.75"
        fill="none"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" fill="none" />
    </svg>
  );

  return (
    <div className="login-page login-page--v2">
      <div
        className={`login-page--v2__popup ${authView === "register" ? "login-page--v2__popup--register" : ""}`}
        role="dialog"
        aria-label={authView === "login" ? "Đăng nhập" : "Đăng ký"}
      >
        <div className="login-page--v2__col-left">
          <img
            className="login-page--v2__robot"
            src={getAssetUrl("/assets/robot.png")}
            alt=""
            width={319}
            height={535}
          />
        </div>

        <div
          className={`login-page--v2__col-right ${authView === "register" ? "login-page--v2__col-right--register" : ""}`}
        >
          <h1 className="login-page--v2__brand">&gt;SEVER HACK XX88&lt;</h1>
          <h2 className="login-page--v2__subtitle">
            {authView === "login" ? "Đăng nhập tài khoản" : "Đăng ký tài khoản"}
          </h2>

          {authView === "login" ? (
            <form
              className="login-page--v2__form"
              id="loginform"
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
            >
              <div className="login-page--v2__field">
                <label className="login-page--v2__label" htmlFor="txtUsername">
                  Tài khoản:
                </label>
                <input
                  className="login-page--v2__input"
                  type="text"
                  id="txtUsername"
                  placeholder="Nhập tên đăng nhập"
                  onChange={(e) => setUserName(e.target.value)}
                  maxLength={16}
                  minLength={4}
                  autoComplete="username"
                  required
                />
              </div>

              <div className="login-page--v2__field">
                <label className="login-page--v2__label" htmlFor="txtPassword">
                  Mật khẩu:
                </label>
                <div className="login-page--v2__input-wrap">
                  <input
                    className="login-page--v2__input login-page--v2__input--password"
                    type={showPassword ? "text" : "password"}
                    id="txtPassword"
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Nhập mật khẩu"
                    maxLength={16}
                    minLength={4}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="login-page--v2__toggle-pw"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  >
                    {eyeIcon}
                  </button>
                </div>
              </div>

              <button type="submit" className="login-page--v2__submit">
                <img src={getAssetUrl("/assets/frame-btn.png")} alt="" />
                <span className="login-page--v2__submit-text">Đăng nhập</span>
              </button>
            </form>
          ) : (
            <form
              className="login-page--v2__form"
              id="registerform"
              onSubmit={handleRegisterSubmit(onRegister)}
              noValidate
            >
              <div className="login-page--v2__field">
                <label className="login-page--v2__label" htmlFor="regUsername">
                  Tài khoản:
                </label>
                <input
                  className="login-page--v2__input"
                  id="regUsername"
                  placeholder="Nhập tên đăng nhập"
                  autoComplete="username"
                  {...registerField("username")}
                />
                {registerErrors.username && (
                  <p className="login-page--v2__error">{registerErrors.username.message}</p>
                )}
              </div>

              <div className="login-page--v2__field">
                <label className="login-page--v2__label" htmlFor="regPhone">
                  Số điện thoại:
                </label>
                <input
                  className="login-page--v2__input"
                  id="regPhone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="Nhập số điện thoại (10 số)"
                  maxLength={10}
                  {...registerField("phone_number")}
                />
                {registerErrors.phone_number && (
                  <p className="login-page--v2__error">{registerErrors.phone_number.message}</p>
                )}
              </div>

              <div className="login-page--v2__field">
                <label className="login-page--v2__label" htmlFor="regPassword">
                  Mật khẩu:
                </label>
                <div className="login-page--v2__input-wrap">
                  <input
                    className="login-page--v2__input login-page--v2__input--password"
                    id="regPassword"
                    type={showRegPassword ? "text" : "password"}
                    placeholder="Nhập mật khẩu (tối thiểu 6 ký tự)"
                    autoComplete="new-password"
                    {...registerField("password")}
                  />
                  <button
                    type="button"
                    className="login-page--v2__toggle-pw"
                    onClick={() => setShowRegPassword((v) => !v)}
                    aria-label={showRegPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  >
                    {eyeIcon}
                  </button>
                </div>
                {registerErrors.password && (
                  <p className="login-page--v2__error">{registerErrors.password.message}</p>
                )}
              </div>

              <div className="login-page--v2__field">
                <label className="login-page--v2__label" htmlFor="regPassword2">
                  Nhập lại mật khẩu:
                </label>
                <div className="login-page--v2__input-wrap">
                  <input
                    className="login-page--v2__input login-page--v2__input--password"
                    id="regPassword2"
                    type={showRegPassword2 ? "text" : "password"}
                    placeholder="Nhập lại mật khẩu"
                    autoComplete="new-password"
                    {...registerField("password_confirmation")}
                  />
                  <button
                    type="button"
                    className="login-page--v2__toggle-pw"
                    onClick={() => setShowRegPassword2((v) => !v)}
                    aria-label={showRegPassword2 ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  >
                    {eyeIcon}
                  </button>
                </div>
                {registerErrors.password_confirmation && (
                  <p className="login-page--v2__error">{registerErrors.password_confirmation.message}</p>
                )}
              </div>

              <button type="submit" className="login-page--v2__submit">
                <img src={getAssetUrl("/assets/frame-btn.png")} alt="" />
                <span className="login-page--v2__submit-text">Đăng ký</span>
              </button>
            </form>
          )}

          {authView === "login" ? (
            <p className="login-page--v2__footer">
              Chưa có tài khoản?{" "}
              <span
                className="login-page--v2__link"
                role="button"
                tabIndex={0}
                onClick={goRegister}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goRegister();
                  }
                }}
              >
                Đăng ký ngay
              </span>
            </p>
          ) : (
            <p className="login-page--v2__footer">
              Đã có tài khoản?{" "}
              <span
                className="login-page--v2__link"
                role="button"
                tabIndex={0}
                onClick={goLogin}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goLogin();
                  }
                }}
              >
                Đăng nhập ngay
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
